// src/pages/SearchPage.jsx
import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  Search, Download, Eye, Loader, ChevronLeft, ChevronRight,
  FileText, AlertCircle, User, Calendar, BookOpen, Book,
  GraduationCap, BarChart, Users, School
} from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import Button from '../components/common/Button';
import { publicAPI } from '../services/api';

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [searchResults, setSearchResults] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Perform search when query changes
  useEffect(() => {
    if (searchQuery) {
      performSearch();
    } else {
      setSearchResults([]); // Clear if empty
    }
  }, [currentPage, searchParams]);

  const performSearch = async () => {
    try {
      setLoading(true);
      setError('');

      const params = {
        search: searchQuery,
        page: currentPage,
        limit: 12
      };

      // Search only courses - using courses endpoint
      const response = await publicAPI.getCourses(params);
      
      console.log('Search response:', response);
      
      if (response.success) {
        // Handle different response structures
        const data = response.data?.courses || response.data || [];
        const pagination = response.pagination || response.data?.pagination || {};
        
        setSearchResults(data);
        setTotalResults(pagination.total || data.length);
        setTotalPages(pagination.pages || 1);
      } else {
        setError('No results found.');
        setSearchResults([]);
      }
    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching.');
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    
    const params = new URLSearchParams();
    params.set('q', searchQuery);
    setSearchParams(params);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      const params = new URLSearchParams(searchParams);
      params.set('q', searchQuery); // Keep search query
      params.set('page', page);
      setSearchParams(params);
    }
  };

  // Navigate to course details - now goes to teacher selection
  const handleCourseClick = (course) => {
    navigate(`/school/${course.school?._id || course.schoolId}/department/${course.department?._id || course.departmentId}/year/${course.level || 1}/course/${course._id}`);
  };

  // Helper to get school name safely
  const getSchoolName = (course) => {
    if (course.school?.name) return course.school.name;
    if (course.schoolName) return course.schoolName;
    return 'School';
  };

  // Helper to get department name safely
  const getDepartmentName = (course) => {
    if (course.department?.name) return course.department.name;
    if (course.departmentName) return course.departmentName;
    return 'Department';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors">
            <ChevronLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>

        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Find Courses</h1>
          <p className="text-gray-600">Search for courses by <span className="font-semibold text-blue-600">Course Name</span> or <span className="font-semibold text-green-600">Course Code</span>.</p>
        </div>

        {/* Search Bar */}
        <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-2 mb-10">
          <form onSubmit={handleSearch} className="flex items-center">
            <Search className="ml-4 w-6 h-6 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="e.g. Data Structures, CS101, Database Systems..."
              className="w-full px-4 py-4 text-lg outline-none bg-transparent"
            />
            <button
              type="submit"
              disabled={loading}
              className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors m-1"
            >
              {loading ? '...' : 'Search Courses'}
            </button>
          </form>
        </div>

        {error && (
          <div className="max-w-3xl mx-auto mb-8 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-800">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        )}

        {/* Results */}
        <div className="max-w-5xl mx-auto">
          {loading && (
            <div className="flex justify-center py-12">
              <Loader className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
          )}

          {!loading && searchResults.length === 0 && searchQuery && !error && (
            <div className="text-center py-12">
              <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                <Book className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900">No courses found</h3>
              <p className="text-gray-500 mt-2">We couldn't find any courses matching "{searchQuery}".</p>
              <p className="text-gray-500 text-sm mt-1">Try searching with different keywords or course codes.</p>
            </div>
          )}

          {!loading && searchResults.length > 0 && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500">Found {totalResults} courses</p>
                <p className="text-xs text-gray-400">Click on any course to view available instructors</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {searchResults.map((course) => (
                  <div 
                    key={course._id}
                    onClick={() => handleCourseClick(course)}
                    className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 hover:border-blue-300 transition-all duration-300 cursor-pointer group"
                  >
                    <div className="p-6">
                      {/* Header with code and credits */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex items-center gap-2">
                          <div className="p-2 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                          </div>
                          <span className="px-3 py-1 bg-blue-50 text-blue-700 text-sm font-bold rounded-lg uppercase">
                            {course.courseCode}
                          </span>
                        </div>
                        <span className="flex items-center gap-1.5 text-gray-500 text-xs font-medium bg-gray-50 px-2 py-1 rounded-md">
                          <BarChart className="w-3.5 h-3.5" /> {course.credits || 3} Credits
                        </span>
                      </div>
                      
                      {/* Course Title */}
                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors line-clamp-2">
                        {course.title}
                      </h3>
                      
                      {/* Description */}
                      {course.description && (
                        <p className="text-gray-600 text-sm line-clamp-2 mb-4">
                          {course.description}
                        </p>
                      )}
                      
                      {/* School and Department Info */}
                      <div className="flex flex-wrap items-center gap-3 mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                          <School className="w-3.5 h-3.5 text-purple-500" />
                          <span>{getSchoolName(course)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                          <GraduationCap className="w-3.5 h-3.5 text-green-500" />
                          <span>{getDepartmentName(course)}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded">
                          <Calendar className="w-3.5 h-3.5 text-orange-500" />
                          <span>Year {course.level || 1}</span>
                        </div>
                      </div>
                      
                      {/* Footer with action */}
                      <div className="mt-4 flex justify-between items-center">
                        <div className="flex items-center gap-2 text-xs text-gray-400">
                          <Users className="w-3.5 h-3.5" />
                          <span>Multiple instructors</span>
                        </div>
                        <span className="text-blue-600 text-sm font-medium group-hover:translate-x-1 transition-transform flex items-center gap-1">
                          View Instructors
                          <ChevronRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-8">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)} 
                    disabled={currentPage === 1} 
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <span className="text-gray-600 font-medium">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)} 
                    disabled={currentPage === totalPages} 
                    className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Initial state - no search yet */}
          {!searchQuery && !loading && searchResults.length === 0 && (
            <div className="text-center py-12">
              <div className="inline-flex p-4 bg-blue-50 rounded-full mb-4">
                <Search className="w-10 h-10 text-blue-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Search for Courses</h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Enter a course name or code above to find available courses across all schools and departments.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">CS101</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">Data Structures</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">Database Systems</span>
                <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">MATH201</span>
              </div>
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SearchPage;